import { bpsToPercentString } from '@/utils/bps';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Chip } from '@heroui/react';
import type { OperationPreview } from '@/api/models';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { EntryShapeRow, RecordError } from '../../recordMeta';

type ImpactPanelProps = {
    preview: OperationPreview | null;
    isPreviewing: boolean;
    /** A refusal that belongs to the whole request rather than to one field. */
    formError: RecordError | null;
    /**
     * The recipe's lines, drawn from the moment an operation is picked. Empty
     * until one is. Replaced by `preview` the instant the server answers.
     */
    shape: EntryShapeRow[];
    /** True once the server's own totals agree — never inferred from the shape. */
    isBalanced: boolean;
    /** `August 2026`, or `null` before an operation is picked. */
    periodLabel: string | null;
    /** The recipe version the entry will post under. */
    version: number | null;
};

/**
 * What pressing Post will do to the books.
 *
 * This panel is the reason a non-accountant can be trusted with the button. It
 * is not a summary of the form — it is **the entry**, in the ledger's own terms:
 * every line with its account code and name, the figure in the right column, and
 * the two totals that prove it balances.
 *
 * Four decisions worth keeping:
 *
 * **It starts explaining at the pick, not at the last field.** Before any amount
 * exists the panel already lists the accounts the recipe will touch, with `—` in
 * both money columns. That view is drawn client-side from the recipe and is
 * **explicitly not authoritative** — see `entryShape`. It carries no Balanced
 * badge and no totals, because both would be claims only the server can make.
 *
 * **The lines come from the server, not from arithmetic here.** `preview` and
 * `record` run one pipeline server-side, so what this shows is what will post —
 * not a client's best guess at it. That identity is the whole guarantee, and it
 * is why an unreadable preview falls back to the shape rather than to something
 * computed locally.
 *
 * **It states that posting is immediate, every time.** Corrections are reversals
 * — there is no draft to fix and no undo — and the one moment a user needs to
 * know that is while looking at the entry they are about to commit. It is a
 * standing line of copy, not a warning that appears when something is wrong.
 *
 * **The VAT rate is named in words.** A figure derived from a rate the reader
 * cannot see is a figure they have to take on trust; a tenant with no rate in
 * force sees that said rather than a silent zero.
 */
export const ImpactPanel: React.FC<ImpactPanelProps> = ({
    preview,
    isPreviewing,
    formError,
    shape,
    isBalanced,
    periodLabel,
    version,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('operations.record.impact.title')}
            className="flex flex-col gap-3 rounded-2xl border border-separator bg-surface p-4"
            {...getNodeTestIdProp('operations-record-impact')}
        >
            <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-foreground">
                    {t('operations.record.impact.title')}
                </span>
                {isBalanced ? (
                    <Chip
                        color="success"
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp(
                            'operations-record-impact-balanced',
                        )}
                    >
                        <Icon aria-hidden icon="gravity-ui:check" />
                        <Chip.Label>
                            {t('operations.record.impact.balanced')}
                        </Chip.Label>
                    </Chip>
                ) : isPreviewing ? (
                    <span
                        className="text-xs text-muted"
                        {...getNodeTestIdProp(
                            'operations-record-impact-loading',
                        )}
                    >
                        {t('operations.record.impact.loading')}
                    </span>
                ) : null}
            </div>

            <table className="w-full text-sm">
                <caption className="sr-only">
                    {t('operations.record.impact.caption')}
                </caption>
                <thead>
                    <tr className="border-b border-separator text-xs uppercase tracking-wide text-muted">
                        <th className="py-1 text-start font-medium" scope="col">
                            {t('operations.record.impact.account')}
                        </th>
                        <th className="py-1 text-end font-medium" scope="col">
                            {t('operations.record.impact.debit')}
                        </th>
                        <th className="py-1 text-end font-medium" scope="col">
                            {t('operations.record.impact.credit')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {preview !== null ? (
                        preview.lines.map((line) => (
                            <tr
                                className="border-b border-separator/60"
                                key={`${line.accountId}-${line.debitTetri}-${line.creditTetri}`}
                                {...getNodeTestIdProp(
                                    `operations-record-impact-line-${line.accountId}`,
                                )}
                            >
                                <th
                                    className="py-1.5 text-start font-normal text-foreground"
                                    scope="row"
                                >
                                    {`${line.code} — ${line.name}`}
                                </th>
                                <td className="py-1.5 text-end tabular-nums">
                                    {line.debitTetri > 0
                                        ? formatGel(line.debitTetri)
                                        : null}
                                </td>
                                <td className="py-1.5 text-end tabular-nums">
                                    {line.creditTetri > 0
                                        ? formatGel(line.creditTetri)
                                        : null}
                                </td>
                            </tr>
                        ))
                    ) : shape.length > 0 ? (
                        shape.map((row) => (
                            <tr
                                className="border-b border-separator/60"
                                key={row.lineId}
                                {...getNodeTestIdProp(
                                    `operations-record-impact-shape-${row.lineId}`,
                                )}
                            >
                                <th
                                    className={
                                        row.isPending
                                            ? 'py-1.5 text-start font-normal text-muted'
                                            : 'py-1.5 text-start font-normal text-foreground'
                                    }
                                    scope="row"
                                >
                                    {row.label}
                                </th>
                                <td className="py-1.5 text-end text-muted tabular-nums">
                                    {row.side === 'DEBIT' ? '—' : null}
                                </td>
                                <td className="py-1.5 text-end text-muted tabular-nums">
                                    {row.side === 'CREDIT' ? '—' : null}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr
                            {...getNodeTestIdProp(
                                'operations-record-impact-empty',
                            )}
                        >
                            <td className="py-4 text-sm text-muted" colSpan={3}>
                                {formError
                                    ? t('operations.record.impact.refused')
                                    : t('operations.record.impact.incomplete')}
                            </td>
                        </tr>
                    )}
                </tbody>
                {preview ? (
                    <tfoot>
                        <tr
                            className="font-medium"
                            {...getNodeTestIdProp(
                                'operations-record-impact-totals',
                            )}
                        >
                            <th className="py-2 text-start" scope="row">
                                {t('operations.record.impact.total')}
                            </th>
                            <td className="py-2 text-end tabular-nums">
                                {formatGel(preview.totalDebitTetri)}
                            </td>
                            <td className="py-2 text-end tabular-nums">
                                {formatGel(preview.totalCreditTetri)}
                            </td>
                        </tr>
                    </tfoot>
                ) : null}
            </table>

            {preview === null && shape.length > 0 ? (
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp(
                        'operations-record-impact-shape-note',
                    )}
                >
                    {t('operations.record.impact.shapeNote')}
                </p>
            ) : null}

            {preview ? (
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('operations-record-impact-rate')}
                >
                    {preview.vatRateBps > 0
                        ? t('operations.record.impact.rate', {
                              rate: bpsToPercentString(preview.vatRateBps),
                              version: preview.version,
                          })
                        : t('operations.record.impact.noRate', {
                              version: preview.version,
                          })}
                </p>
            ) : null}

            {periodLabel ? (
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('operations-record-impact-footer')}
                >
                    {t('operations.record.impact.postsTo', {
                        period: periodLabel,
                        version: preview?.version ?? version ?? 1,
                    })}
                </p>
            ) : null}

            <p
                className="flex items-start gap-2 text-xs text-muted"
                {...getNodeTestIdProp('operations-record-impact-immediate')}
            >
                <Icon aria-hidden className="mt-0.5" icon="gravity-ui:lock" />
                {t('operations.record.impact.immediate')}
            </p>
        </section>
    );
};
